<template>
  <div>
    <attribute-creator
      @close="attributeCreatorActive = false"
      v-if="attributeCreatorActive"
      @save="saveAttribute($event)"
    />
    <sync-icon/>
    <markdown-editor :attribute="attributeOnEditor" @close="attributeOnEditor = null" @update="updateAttribute(attributeOnEditor, $event)"/>
    <route @change-route="openDocument"/>
    <div class="flex flex-wrap justify-center mt-2 mb-20">
      <div class="flex justify-center w-full">
        <div class="bg-gray-200 hover:bg-white rounded-full h-10 w-10 flex items-center justify-center text-gray-700 cursor-pointer"
             @click="attributeCreatorActive = true">
          <icon icon="plus"/>
        </div>
      </div>
      <div class="w-full lg:w-1/2 justify-center mt-3">
        <transition-group name="flip-list" tag="div">
          <div v-for="(attribute, index) in attributes"
               :key="attribute._id"
               class="flex flex-wrap relative"
               @click="selectedAttributeId = attribute._id"
               @mouseover="selectedAttributeId = attribute._id"
               >
            <div class="hidden sm:block w-8">
              <div class="flex flex-col h-full justify-center items-center" v-if="selectedAttributeId === attribute._id">
                <icon class="text-xl cursor-pointer" @click="moveAttributeUp(index)" icon="caret-up" v-if="index !== 0"/>
                <icon class="text-xl cursor-pointer" @click="moveAttributeDown(index)" icon="caret-down" v-if="index !== attributes.length - 1"/>
              </div>
            </div>
            <div class="flex-1 rounded py-3 border border-transparent"
                 :class="{selected: selectedAttributeId === attribute._id, 'border-gray-500': openedMenu === attribute._id }">
              <div class="block sm:hidden h-5 w-full flex justify-end">
                <icon class="text-xs" @click="openedMenu = attribute._id" icon="chevron-down"/>
              </div>
              <audio-record v-if="attribute.type === 'audio'"
                            :attribute="attribute"
                            :edit-name="nameOnEdit === attribute._id"
                            @cancel-edit="nameOnEdit = ''"
                            @upload-attachment="uploadAttachment"
                            @rename="renameAttribute(attribute, $event)"
                            :document-id="documentId"
              />
              <boolean v-else-if="attribute.type === 'boolean'"
                       :attribute="attribute"
                       :edit-name="nameOnEdit === attribute._id"
                       @cancel-edit="nameOnEdit = ''"
                       @update="updateAttribute(attribute, $event)"
                       @rename="renameAttribute(attribute, $event)"
              />
              <command-type v-else-if="attribute.type === 'command'"
                            :attribute="attribute"
                            :edit-name="nameOnEdit === attribute._id"
                            @cancel-edit="nameOnEdit = ''"
                            @update="updateAttribute(attribute, $event)"
                            @rename="renameAttribute(attribute, $event)"
              />
              <folder v-else-if="attribute.type === 'folder'"
                      :attribute="attribute"
                      @open="openDocument(attribute.value)"
                      :edit-name="nameOnEdit === attribute._id"
                      @cancel-edit="nameOnEdit = ''"
                      @rename="renameAttribute(attribute, $event)"
              />
              <markdown v-else-if="attribute.type === 'markdown'"
                        :attribute="attribute"
                        @open="attributeOnEditor = attribute"
                        :edit-name="nameOnEdit === attribute._id"
                        @cancel-edit="nameOnEdit = ''"
                        @rename="renameAttribute(attribute, $event)"
              />
              <plain-text v-else-if="attribute.type === 'text'"
                          :attribute="attribute"
                          :edit-name="nameOnEdit === attribute._id"
                          @cancel-edit="nameOnEdit = ''"
                          @update="updateAttribute(attribute, $event)"
                          @rename="renameAttribute(attribute, $event)"
              />
              <note v-else
                    :attribute="attribute"
                    @update="updateAttribute(attribute, $event)"
              />
            </div>
            <div class="hidden sm:block w-8">
              <div class="flex flex-col h-full justify-center items-center" v-show="selectedAttributeId === attribute._id">
                <icon class="text-md cursor-pointer hover:text-white" @click="openedMenu = attribute._id" icon="ellipsis-v" />
              </div>
            </div>
<!--             context menu shadow-->
            <div class="fixed inset-0 bg-black opacity-25"
                 @click="openedMenu = ''"
                 v-show="openedMenu === attribute._id"/>
<!--            context menu-->
            <div class="absolute bg-white top-0 right-0 rounded text-gray-600 shadow-2xl overflow-hidden z-50"
                 v-show="openedMenu === attribute._id">
              <div class="hover:bg-blue-300 hover:text-white px-2 py-1 cursor-pointer text-center text-lg"
                   @click="allowAttributeRename(attribute)"
                   v-show="attribute.type !== 'note'">
                rename
              </div>
<!--              <div class="hover:bg-blue-300 hover:text-white px-2 py-1 cursor-pointer text-center">move locally</div>-->
<!--              <div class="hover:bg-blue-300 hover:text-white px-2 py-1 cursor-pointer text-center">move to...</div>-->
              <div class="hover:bg-blue-300 hover:text-white px-2 py-1 cursor-pointer text-center" @click="moveAttributeUp(index)">move up</div>
              <div class="hover:bg-blue-300 hover:text-white px-2 py-1 cursor-pointer text-center" @click="moveAttributeDown(index)">move down</div>
              <div class="hover:bg-blue-300 hover:text-white px-2 py-1 cursor-pointer text-center" v-show="!deleteAcknowledgeActive" @click="deleteAcknowledgeActive = true">delete</div>
              <div class="cursor-pointer text-center flex" v-show="deleteAcknowledgeActive" @click="deleteAttribute(index)" @mouseleave="deleteAcknowledgeActive = false">
                <div class="w-1/2 px-2 py-1 hover:bg-blue-300 hover:text-white">yes</div>
                <div class="w-1/2 px-2 py-1 hover:bg-blue-300 hover:text-white">no</div>
              </div>
            </div>
            <div class="w-full divider"></div>
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import getUUID from 'uuid/v4'
import database from '@/database'
import 'tui-editor/dist/tui-editor.css'
import 'tui-editor/dist/tui-editor-contents.css'
import 'codemirror/lib/codemirror.css'
import AttributeCreator from '@/components/AttributeCreator'
import editorConfig from '@/config/editor.js'
import MarkdownEditor from '@/components/MarkdownEditor'
import SyncIcon from '@/components/SyncIcon'
import PlainText from '@/components/attributes/PlainText'
import Boolean from '@/components/attributes/Boolean'
import Folder from '@/components/attributes/Folder'
import Markdown from '@/components/attributes/Markdown'
import Note from '@/components/attributes/Note'
import AudioRecord from '@/components/attributes/AudioRecord'
import CommandType from '@/components/attributes/CommandType'
import { isUndefined } from 'lodash'

export default {
  components: {
    PlainText,
    AttributeCreator,
    Boolean,
    Folder,
    Markdown,
    MarkdownEditor,
    Note,
    SyncIcon,
    AudioRecord,
    CommandType
  },
  data () {
    return {
      name: '',
      attributes: [],
      attachments: [],
      isReusingComponent: false,
      editorConfig: editorConfig,
      documentId: '',
      documentRevision: '',
      openedMenu: '',
      syncObject: {},
      firstSync: true,
      attributeCreatorActive: false,
      attributeOnEditor: {},
      selectedAttributeId: null,
      deleteAcknowledgeActive: false,
      nameOnEdit: ''
    }
  },
  mounted () {
    let documentId = this.$route.params.id
    isUndefined(documentId) ? this.documentId = 'index' : this.documentId = documentId
    if (this.documentId === 'index.html') this.documentId = 'index'
    database.createDatabases()
    this.syncDatabases()
  },
  beforeRouteUpdate (to, from, next) {
    this.attributes = []
    this.documentId = to.params.id
    this.fetchDocument()
    next()
  },
  beforeDestroy () {
    this.syncObject.cancel()
  },
  methods: {
    async syncDatabases () {
      this.$store.commit('startLoading')
      this.syncObject = database.syncDatabases()
      this.syncObject.on('change', ({ change }) => {
        if (change.docs[0]._rev === this.documentRevision) return
        if (change.docs[0]._id !== this.documentId) return
        this.$store.commit('pushLog', 'Change received from remote')
        this.fetchDocument()
      })
      this.syncObject.on('paused', (info) => {
        if (this.firstSync) {
          this.fetchDocument()
          this.firstSync = false
          this.$store.commit('endLoading')
        }
      })
    },
    async fetchDocument () {
      let document = await database.fetchDocument(this.documentId)
      this.attributes = [...document.attributes]
      this.attachments = document._attachments ? document._attachments : {}
      this.name = document.name
      this.documentRevision = document._rev
      let routeToPush = { name: document.name, documentId: document._id }
      this.$store.commit('pushRoute', routeToPush)
      this.$nextTick(() => this.attributes.sort((a, b) => a.order - b.order))
    },
    updateAttribute (attribute, newValue) {
      attribute.value = newValue
      this.updateDocument()
    },
    async renameAttribute (attribute, newName) {
      if (attribute.type === 'folder') {
        let document = await database.fetchDocument(attribute.value)
        document.name = newName
        await database.updateDocument(document)
      }
      attribute.name = newName
      this.updateDocument()
    },
    async uploadAttachment (attribute, blob, type) {
      this.$store.commit('startLoading')
      let response = await database.uploadAttachment(this.documentId, attribute._id, this.documentRevision, blob, type)
      this.$store.commit('endLoading')
      this.documentRevision = response.rev
      await this.fetchDocument()
      let attributeFetched = this.attributes.find(attr => attr._id === attribute._id)
      attributeFetched.attachment = attribute._id
      this.updateDocument()
    },
    async moveAttributeUp (attributeIndex) {
      this.openedMenu = ''
      this.attributes[attributeIndex - 1].order++
      this.attributes[attributeIndex].order--
      this.attributes.sort((a, b) => a.order - b.order)
      await this.updateDocument()
    },
    async moveAttributeDown (attributeIndex) {
      this.openedMenu = ''
      this.attributes[attributeIndex + 1].order--
      this.attributes[attributeIndex].order++
      this.attributes.sort((a, b) => a.order - b.order)
      await this.updateDocument()
    },
    openDocument (documentId) {
      this.$router.push({ name: 'document', params: { id: documentId } })
    },
    async saveDocument (name) {
      let document = {}
      document._id = getUUID()
      document.name = name
      document.attributes = []
      return database.storeDocument(document)
    },
    allowAttributeRename (attribute) {
      this.openedMenu = ''
      this.nameOnEdit = attribute._id
    },
    async deleteDocument (documentId) {
      this.deleteAcknowledgeActive = false
      let indexToDelete = this.subdocumentsIds.indexOf(documentId)
      if (indexToDelete !== -1) {
        this.subdocumentsIds.splice(indexToDelete, 1)
      } else {
        this.fetchDocument()
        return
      }
      await this.updateDocument()
      await database.deleteDocument(documentId)
      this.fetchDocument()
    },
    async updateDocument () {
      let documentChanges = {
        _id: this.documentId,
        _rev: this.documentRevision,
        name: this.name,
        attributes: this.attributes,
        subdocumentsIds: this.subdocumentsIds,
        _attachments: this.attachments
      }
      let updatedDocument = await database.updateDocument(documentChanges)
      this.documentRevision = updatedDocument.rev
    },
    async saveAttribute (attribute) {
      let newAttribute = {
        _id: getUUID(),
        type: attribute.type,
        name: attribute.name,
        value: '',
        order: this.attributes.length + 1,
        created_at: this.now()
      }
      if (newAttribute.type === 'folder') {
        newAttribute.value = await this.saveDocument(newAttribute.name)
      }
      if (newAttribute.type === 'note') {
        newAttribute.value = newAttribute.name
      }
      if (newAttribute.type === 'boolean') {
        newAttribute.value = false
      }
      this.attributes.push(newAttribute)
      this.updateDocument()
    },
    async deleteAttribute (attributeIndex) {
      this.deleteAcknowledgeActive = false
      this.$store.commit('startLoading')
      let attribute = this.attributes[attributeIndex]
      if (attribute.type === 'folder') {
        await database.deleteDocument(attribute.value)
      }
      this.attributes.splice(attributeIndex, 1)
      let cont = 1
      this.attributes.forEach(attribute => {
        attribute.order = cont
        cont++
      })
      if ('attachment' in attribute) {
        const response = await database.deleteAttachment(this.documentId, attribute.attachment, this.documentRevision)
        this.documentRevision = response.rev
        delete this.attachments[attribute.attachment]
      }
      this.updateDocument()
      this.$store.commit('endLoading')
    }
  },
  watch: {
    openedMenu () {
      this.deleteAcknowledgeActive = false
    }
  }
}
</script>

<style lang="scss">
.divider {
  height: 1px;
  border-bottom: solid  0.5px rgba(0, 0, 0, 0.49);
}
.menu {
  .active {
    transform: translateX(0px);
    position: absolute;
    right: 0;
    top: 17px;
    z-index: 1000;
    background: white;
    border-radius: 4px;
    text-align: left;

    ul {
      display: inline-block;
      padding: 0;
      margin: 0;
      list-style: none;

      li {
        cursor: pointer;
        padding: 2px 10px;

        &:hover {
          background-color: #d6d6d6;
        }
      }
    }
  }

  svg {
    cursor: pointer;
    color: white;
    justify-self: center;

    &:hover {
      transform: scale(1.2);
    }
  }
}

input.name {
  word-break: break-word;
  background-color: rgba(255, 255, 255, 0.12);
  box-sizing: border-box;
  &:focus, &:hover {
    background-color: rgba(255, 255, 255, 0.21);
  }
}
.selected {
  background-color: rgba(44, 57, 76, 0.05);
}

.selected {
  border: solid 1px white;
}

.flip-list-move {
  transition: transform 0.5s;
}

</style>
